import React from 'react'
import { Icon } from '@mobi/component-library/Common/Icon'
import { registerBodyScrollLock, deregisterBodyScrollLock } from '@mobi/utils/functions'
import {
  GiddyUpTabChanged,
  QuaddieValueChanged,
} from '@classic/AppUtils/Framework/Intent/giddyUpSignals'
import * as EventAggregator from '@classic/AppUtils/Framework/Messaging/EventAggregator'
import { GiddyUpModalCloseTopic } from '@classic/Betting-v2/Components/Pages/Starters/constants'
import { setGiddyUpTab, setGiddyUpQuaddieValue } from '@classic/Foundation/Analytics/Analytics'
import { MainContainer } from '@core/Components/Containers'
import { OverlayClose } from '@core/Components/Overlay'
import {
  OverlayCloseButtonStyled,
  OverlayHeaderStyled,
  OverlayHeadingStyled,
} from '@core/Components/Overlay/Overlay.styles'
import { GiddyUp } from './Components/GiddyUp'
import { Initialise, Teardown } from './states/signals'
import { page$ } from './states/page'
import {
  clearGetAcceptorFunc,
  clearSelection,
  ClearSelectionFunc,
  RaceDetailFunc,
  SelectAcceptorFunc,
  SelectQuaddieFunc,
  setClearSelection,
  setGetAcceptorFunc,
  setGetRaceDetailFunc,
  setSelectAcceptorFunc,
  setSelectQuaddieFunc,
} from './integration'
import type { AcceptorFunc } from './types'
import { deregisterSideEffects, registerSideEffects } from './side-effects'
import { GiddyUpRaces } from './data-access/giddyUpRaces'
import { GiddyUpQuaddieTips } from './data-access/giddyUpQuaddieTips'

interface IRaceInformation {
  raceKey: string
  fixtureKey: string
  getAcceptor: AcceptorFunc
  getRaceDetail: RaceDetailFunc
  selectAcceptor: SelectAcceptorFunc
  clearSelection: ClearSelectionFunc
  selectQuaddie: SelectQuaddieFunc
}

export const GiddyUpPage: React.FC<{ raceInfo: IRaceInformation }> = ({ raceInfo }) => {
  const containerElementRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    registerSideEffects()
    setGetAcceptorFunc(raceInfo.getAcceptor)
    setGetRaceDetailFunc(raceInfo.getRaceDetail)
    setSelectAcceptorFunc(raceInfo.selectAcceptor)
    setClearSelection(raceInfo.clearSelection)
    setSelectQuaddieFunc(raceInfo.selectQuaddie)

    const promises: Promise<unknown>[] = [GiddyUpRaces.getPromise(raceInfo.raceKey)]
    if (raceInfo.fixtureKey) promises.push(GiddyUpQuaddieTips.getPromise(raceInfo.fixtureKey))

    Promise.all(promises)
    Initialise({ raceKey: raceInfo.raceKey })

    return () => {
      EventAggregator.publish(GiddyUpModalCloseTopic)
      page$.first().subscribe(lastState => {
        if (lastState && lastState.acceptorSelected) clearSelection()
      })
      deregisterSideEffects()
      clearGetAcceptorFunc()
      Teardown()
    }
  }, [raceInfo])

  React.useEffect(() => {
    if (!containerElementRef.current) return
    const containerElement = containerElementRef.current
    registerBodyScrollLock(containerElement)
    return () => deregisterBodyScrollLock(containerElement)
  }, [])

  React.useEffect(() => {
    const tabChangeSubscription = GiddyUpTabChanged.signal$.subscribe(x => {
      setGiddyUpTab(x.data)
    })
    const quaddieChangeSubscription = QuaddieValueChanged.signal$.subscribe(x => {
      setGiddyUpQuaddieValue(x.data)
    })
    return () => {
      tabChangeSubscription.dispose()
      quaddieChangeSubscription.dispose()
    }
  }, [])

  return (
    <div ref={containerElementRef} style={{ overflowY: 'auto' }}>
      <OverlayHeaderStyled>
        <OverlayHeadingStyled>Giddy-Up</OverlayHeadingStyled>
        <OverlayCloseButtonStyled data-testid='close-button' onClick={OverlayClose}>
          <Icon type='cross' title='Close' />
        </OverlayCloseButtonStyled>
      </OverlayHeaderStyled>

      <MainContainer background='grey'>
        <GiddyUp raceKey={raceInfo.raceKey} fixtureKey={raceInfo.fixtureKey} />
      </MainContainer>
    </div>
  )
}
